"use client";

import useConstantValueList from "@/hooks/useConstantValueList";
import useFormSubmit from "@/hooks/useApiSubmit";
import useServiceList from "@/hooks/useServiceList";
import {
  Button,
  Card,
  DatePickerField,
  Field,
  Fieldset,
  Form,
  PhoneField,
  Radio,
  RadioGroup,
  Select,
} from "@repo/ui";
import { useRouter } from "next/navigation";
import { useMemo } from "react";
import * as Yup from "yup";
import { useAppDispatch } from "@/store/hooks";
import { setFullName } from "@/store/features/appSlice";
import TermsCheckbox from "./TermsCheckbox";
import { useCountryCode } from "@/hooks/useCountryCode";

const DEFAULT_FORM_VALUES = {
  fullName: "",
  contactNumber: "",
  email: "",
  serviceId: [] as string[],
  whoAreWe: "173",
  enquiryType: "COMMON_REQUEST_CALLBACK",
  terms: "false",
  requestDate: "",
  requestTime: "",
};

const QueryFormSchema = Yup.object().shape({
  fullName: Yup.string()
    .required()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  contactNumber: Yup.string()
    .required()
    .min(10, "Too Short!")
    .max(16, "Too Long!")
    .required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
});

type RequestCallbackFormProps = {
  onSuccess?: () => void;
};

function RequestCallbackForm(props: RequestCallbackFormProps) {
  const router = useRouter();
  const { data: services } = useServiceList();
  const { data: whoWeAre } = useConstantValueList();
  const dispatch = useAppDispatch();

  const { country } = useCountryCode();

  const { handleSubmit } = useFormSubmit({
    onSuccess: (data) => {
      dispatch(setFullName(data.fullName));
      props.onSuccess?.();
      router.push("/thank-you-for-enquiry");
    },
  });

  const formContent = useMemo(
    () => (
      <Form
        initialValues={DEFAULT_FORM_VALUES}
        validationSchema={QueryFormSchema}
        onSubmit={handleSubmit}
        className="p-1"
      >
        {({ values, handleChange, setFieldValue, isSubmitting }) => (
          <>
            <Fieldset columns={2}>
              <Field
                name="fullName"
                label="Full Name"
                value={values.fullName}
                onChange={handleChange}
              />
              <PhoneField
                label="Phone Number"
                name="contactNumber"
                required
                value={values.contactNumber}
                onChange={(phone: string) =>
                  setFieldValue("contactNumber", phone)
                }
                defaultCountry={country}
              />
              <Field
                label="Email ID"
                name="email"
                value={values.email}
                onChange={handleChange}
              />
              <Select
                label="Courses"
                name="serviceId"
                options={services}
                value={values.serviceId}
                multiple
                onChange={(value: string[]) =>
                  setFieldValue("serviceId", value)
                }
              />

              <DatePickerField
                label="Date"
                value={values.requestDate}
                onChange={(value) => setFieldValue("requestDate", value)}
                calenderProps={{ disabled: { before: new Date() } }}
              />
              <Field
                label="Time"
                name="requestTime"
                value={values.requestTime}
                type="time"
                onChange={handleChange}
              />
              <RadioGroup
                label="Enquiry for"
                value={values.whoAreWe}
                name="whoAreWe"
                defaultValue={values.whoAreWe}
                onValueChange={(value) => setFieldValue("whoAreWe", value)}
              >
                {whoWeAre.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </Fieldset>

            <TermsCheckbox
              onCheckedChange={(value) => setFieldValue("terms", value)}
              value={values.terms}
            />

            <div className="text-right mt-2">
              <Button type="submit" color="primary" loading={isSubmitting}>
                Submit
              </Button>
            </div>
            {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
          </>
        )}
      </Form>
    ),
    [country, handleSubmit, services, whoWeAre]
  );

  return (
    <Card className="bg-white pt-5" elevation="none">
      {formContent}
    </Card>
  );
}

export default RequestCallbackForm;
